import { CustomModal } from "components/custom-modal";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import {
  CustomTableColor,
  CustomTableColorColumn,
} from "components/dynamic-table/color-table";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Text,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { ExtratoEndpoints, ExtratoRow } from "./utils";
import { CloseIcon } from "components/vectors/close-icon";

const eventName = "openModalConsultaExtratoOnline";

export function ModalConsultaExtratoOnline() {
  const [printing, setPrinting] = useState(false);
  const { isOpen, onClose, params } = useModalControls<ExtratoRow>(eventName);
  const extratoData = params?.json;
  const dados = extratoData?.dados;

  const formatCurrency = (value?: number | null) => {
    if (value == null || Number.isNaN(Number(value))) return "-";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(value));
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("pt-BR");
    }
    return value;
  };

  const formatBool = (value?: boolean | null) => (value ? "SIM" : "NÃO");

  const formatCompetencia = (value?: string | null) => {
    if (!value) return null;
    const ano = value.substring(0, 4);
    const mes = value.substring(4, 6);
    return mes + "/" + ano;
  }

  const contratosEmprestimoColumns = useMemo<CustomTableColorColumn[]>(
    () => [
      {
        name: "Empréstimo",
        render: (row: any) => row.contrato || "-",
      },
      {
        name: "CBC/Banco",
        render: (row: any) =>
          row.banco ? `${row.banco.codigo} - ${row.banco.descricao}` : "-",
      },
      {
        name: "Comp. 1ª",
        render: (row: any) => formatCompetencia(row.competenciaInicioDesconto) || "-",
      },
      {
        name: "Comp. Última Parcela",
        render: (row: any) => formatCompetencia(row.competenciaFimDesconto) || "-",
      },
      {
        name: "Data Inclusão",
        render: (row: any) => formatDate(row.dataInclusao),
      },
      {
        name: "Parcelas",
        render: (row: any) => row.quantidadeParcelas ?? "-",
      },
      {
        name: "Valor Parcela",
        render: (row: any) => formatCurrency(row.valorParcela),
      },
      {
        name: "Valor Empréstimo",
        render: (row: any) => formatCurrency(row.valorEmprestado),
      },
    ],
    [],
  );

  const contratosRmcColumns = useMemo<CustomTableColorColumn[]>(
    () => [
      {
        name: "Nº Contrato",
        render: (row: any) => row.contrato || "-",
      },
      {
        name: "CBC/Banco",
        render: (row: any) =>
          row.banco ? `${row.banco.codigo} - ${row.banco.descricao}` : "-",
      },
      {
        name: "Data Inclusão",
        render: (row: any) => formatDate(row.dataInclusao),
      },
      {
        name: "Limite",
        render: (row: any) => formatCurrency(row.valorLimiteCartao),
      },
      {
        name: "Valor",
        render: (row: any) => formatCurrency(row.valorReservado),
      },
    ],
    [],
  );

  const contratosRccColumns = useMemo<CustomTableColorColumn[]>(
    () => [
      {
        name: "Nº Contrato",
        render: (row: any) => row.contrato || "-",
      },
      {
        name: "CBC/Banco",
        render: (row: any) =>
          row.banco ? `${row.banco.codigo} - ${row.banco.descricao}` : "-",
      },
      {
        name: "Data Inclusão",
        render: (row: any) => formatDate(row.dataInclusao),
      },
      {
        name: "Limite",
        render: (row: any) =>
          row.valorLimiteCartao != null
            ? formatCurrency(row.valorLimiteCartao)
            : "-",
      },
      {
        name: "Valor",
        render: (row: any) =>
          row.valorReservado != null ? formatCurrency(row.valorReservado) : "-",
      },
    ],
    [],
  );

  const handlePrintPdf = async () => {
    if (!params?.hash || printing) return;
    try {
      setPrinting(true);
      const html = await ExtratoEndpoints.getExtratoPDF(params.hash);
      // 1. Cria um iframe oculto
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      // 2. Escreve o conteúdo HTML no iframe
      const doc = iframe.contentWindow?.document;
      doc?.open();
      doc?.write(html);
      doc?.close();

      // 3. Dispara a impressão assim que o conteúdo carregar
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();

      // 4. Remove o iframe depois de imprimir (opcional)
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    } catch {
      // ignore printing errors
    } finally {
      setPrinting(false);
    }
  };

  return (
    <CustomModal
      modalTitle="Demonstrativo de Empréstimos Consignados"
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      scroll="inside"
      modalFooter={
        <>
          <Button onClick={handlePrintPdf} isLoading={printing}>
            Imprimir PDF
          </Button>
          <Button onClick={onClose} leftIcon={<CloseIcon />} variant="outline">
            Fechar
          </Button>
        </>
      }
    >
      {!extratoData ? (
        <Box p="16px">
          <Text color="gray.500">Nenhum dado de extrato disponível.</Text>
        </Box>
      ) : (
        <Grid gap="16px">
          <Card variant="outline">
            <CardHeader py="12px">
              <Text fontWeight="bold">Identificação</Text>
            </CardHeader>
            <CardBody pt="0">
              <Grid templateColumns="2fr 1fr" gap="12px">
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Nome
                  </Text>
                  <Text fontWeight="medium">{dados?.nome || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Benefício
                  </Text>
                  <Text fontWeight="medium">{dados?.beneficio || "-"}</Text>
                </Box>
              </Grid>
            </CardBody>
          </Card>

          <Card variant="outline">
            <CardHeader py="12px">
              <Text fontWeight="bold">Dados do Benefício</Text>
            </CardHeader>
            <CardBody pt="0">
              <Grid templateColumns="2fr 1fr" gap="12px" mb="12px">
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Espécie
                  </Text>
                  <Text fontWeight="medium">
                    {dados?.especie
                      ? `${dados.especie.codigo} - ${dados.especie.descricao}`
                      : "-"}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Situação
                  </Text>
                  <Badge colorScheme="green" variant="subtle">
                    {dados?.situacao || "-"}
                  </Badge>
                </Box>
              </Grid>
              <Grid
                templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                gap="12px"
              >
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    É Pensão Alimentícia
                  </Text>
                  <Text fontWeight="medium">
                    {formatBool(dados?.pensaoAlimenticia)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Possui Procurador
                  </Text>
                  <Text fontWeight="medium">
                    {formatBool(dados?.possuiProcurador)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Possui Representante Legal
                  </Text>
                  <Text fontWeight="medium">
                    {formatBool(dados?.possuiRepresentante)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Bloqueado para Empréstimo
                  </Text>
                  <Text fontWeight="medium">
                    {formatBool(dados?.bloqueioEmprestismo)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Elegível Empréstimo
                  </Text>
                  <Text fontWeight="medium">
                    {formatBool(dados?.elegivelEmprestimo)}
                  </Text>
                </Box>
              </Grid>
            </CardBody>
          </Card>

          <Card variant="outline">
            <CardHeader py="12px">
              <Text fontWeight="bold">Margem Consignável</Text>
            </CardHeader>
            <CardBody pt="0">
              <Grid templateColumns="1.2fr repeat(3, 1fr)" gap="12px">
                <GridItem />
                <Text fontWeight="bold">Empréstimos</Text>
                <Text fontWeight="bold">RMC</Text>
                <Text fontWeight="bold">RCC</Text>

                <Text color="gray.500">Margem Utilizada</Text>
                <Text>{formatCurrency(dados?.margemUtilizadaEmprestimo)}</Text>
                <Text>{formatCurrency(dados?.margemReservadaRMC)}</Text>
                <Text>{formatCurrency(dados?.margemReservadaRCC)}</Text>

                <Text color="gray.500">Margem Disponível</Text>
                <Text>{formatCurrency(dados?.margemDisponivelEmprestimo)}</Text>
                <Text>{formatCurrency(dados?.margemDisponivelRMC)}</Text>
                <Text>{formatCurrency(dados?.margemDisponivelRCC)}</Text>
              </Grid>
            </CardBody>
          </Card>

          <Card variant="outline">
            <CardHeader py="12px">
              <Text fontWeight="bold">Instituição Pagadora</Text>
            </CardHeader>
            <CardBody pt="0">
              <Grid templateColumns="2fr 1fr 1fr 1fr" gap="12px">
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    CBC/Banco
                  </Text>
                  <Text fontWeight="medium">
                    {dados?.banco
                      ? `${dados.banco.codigo} - ${dados.banco.descricao}`
                      : "-"}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Tipo
                  </Text>
                  <Text fontWeight="medium">{dados?.meioPagamento || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Ag.
                  </Text>
                  <Text fontWeight="medium">{dados?.agencia || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    C/C
                  </Text>
                  <Text fontWeight="medium">{dados?.conta || "-"}</Text>
                </Box>
              </Grid>
            </CardBody>
          </Card>

          <Card variant="outline">
            <CardHeader py="12px">
              <Text fontWeight="bold">Contratos de Empréstimos</Text>
            </CardHeader>
            <CardBody pt="0">
              <CustomTableColor
                columns={contratosEmprestimoColumns}
                rows={extratoData?.contratosEmprestimo ?? []}
                showControls={false}
                emptyMessage="Nenhum contrato de empréstimo encontrado."
              />
            </CardBody>
          </Card>

          <Card variant="outline">
            <CardHeader py="12px">
              <Text fontWeight="bold">Contrato RMC</Text>
            </CardHeader>
            <CardBody pt="0">
              <CustomTableColor
                columns={contratosRmcColumns}
                rows={extratoData?.contratosRMC ?? []}
                showControls={false}
                emptyMessage="Nenhum contrato RMC encontrado."
              />
            </CardBody>
          </Card>

          <Card variant="outline">
            <CardHeader py="12px">
              <Text fontWeight="bold">Contrato RCC</Text>
            </CardHeader>
            <CardBody pt="0">
              <CustomTableColor
                columns={contratosRccColumns}
                rows={extratoData?.contratosRCC ?? []}
                showControls={false}
                emptyMessage="Nenhum contrato RCC encontrado."
              />
            </CardBody>
          </Card>
        </Grid>
      )}
    </CustomModal>
  );
}

export const openModalConsultaExtratoOnline =
  createModalTrigger<ExtratoRow>(eventName);
